// Calcolo statistiche live partita

/**
 * Calcola statistiche per un atleta
 * @param {Array} actions - tutte le azioni della sessione
 * @param {string} athlete - 'blue' | 'red'
 * @returns {Object} statistiche atleta
 */
export function calculateAthleteStats(actions, athlete) {
  const myActions   = actions.filter(a => a.athlete === athlete);
  const oppAthlete  = athlete === 'blue' ? 'red' : 'blue';
  const oppActions  = actions.filter(a => a.athlete === oppAthlete);

  const myAttacks   = myActions.filter(a => a.action_type === 'a');
  const myDefenses  = myActions.filter(a => a.action_type === 'd');
  const myCounters  = myActions.filter(a => ['c','ca','ci','c2'].includes(a.action_type));

  // Efficienza attacco: punti in attacco / totale attacchi * 100
  const attackPoints = myAttacks.filter(a => a.outcome === 'punto').reduce((s,a) => s + (a.points||0), 0);
  const attackEfficiency = myAttacks.length > 0
    ? Math.round((myAttacks.filter(a => a.outcome === 'punto').length / myAttacks.length) * 100)
    : 0;

  // Resistenza difensiva: miss + tocco avversario / totale attacchi avversario * 100
  const oppAttacks = oppActions.filter(a => a.action_type === 'a');
  const defenseEfficiency = oppAttacks.length > 0
    ? Math.round((oppAttacks.filter(a => a.outcome !== 'punto').length / oppAttacks.length) * 100)
    : 0;

  // Distribuzione tecniche
  const techniqueCount = {};
  myActions.forEach(a => {
    const tech = a.technique === 'custom' ? (a.custom_technique || 'custom') : a.technique;
    techniqueCount[tech] = (techniqueCount[tech] || 0) + 1;
  });

  // Punti totali segnati
  const totalPoints = myActions
    .filter(a => a.outcome === 'punto')
    .reduce((s, a) => s + (a.points || 0), 0);

  // Punti subiti (= punti segnati dall'avversario contro di me)
  const pointsConceded = oppActions
    .filter(a => a.outcome === 'punto')
    .reduce((s, a) => s + (a.points || 0), 0);

  // Tecniche con cui l'avversario ha segnato contro di me
  const techniquesReceivedCount = {};
  oppActions
    .filter(a => a.outcome === 'punto')
    .forEach(a => {
      const tech = a.technique === 'custom' ? (a.custom_technique || 'custom') : a.technique;
      techniquesReceivedCount[tech] = (techniquesReceivedCount[tech] || 0) + 1;
    });

  return {
    totalActions:            myActions.length,
    totalAttacks:            myAttacks.length,
    totalDefenses:           myDefenses.length,
    totalCounters:           myCounters.length,
    totalPoints,
    pointsConceded,
    attackPoints,
    attackEfficiency,
    defenseEfficiency,
    techniqueCount,
    techniquesReceivedCount,
    successRate: myActions.length > 0
      ? Math.round((myActions.filter(a => a.outcome === 'punto').length / myActions.length) * 100)
      : 0,
  };
}

/**
 * Calcola sequenze tattiche (bigrams o trigrams) per un atleta.
 * Una sequenza è una coppia/tripla di tecniche consecutive nell'ordine temporale.
 * @param {Array}  actions  - tutte le azioni della sessione
 * @param {string} athlete  - 'blue' | 'red'
 * @param {number} n        - lunghezza sequenza (2 = bigram, 3 = trigram)
 * @returns {Array} sequenze ordinate per frequenza decrescente
 *   Ogni elemento: { seq, count, scored, points }
 *   - seq    : stringa tipo "Bn→Dl" con ID tecniche (custom = nome libero)
 *   - count  : quante volte la sequenza è apparsa
 *   - scored : quante volte l'ultima tecnica ha segnato punto
 *   - points : punti totali segnati dall'ultima tecnica della sequenza
 */
export function calculateTacticalSequences(actions, athlete, n = 2) {
  const sorted = actions
    .filter(a => a.athlete === athlete)
    .sort((a, b) => (a.timestamp_video || 0) - (b.timestamp_video || 0));

  if (sorted.length < n) return [];

  const sequences = {};

  for (let i = 0; i <= sorted.length - n; i++) {
    const slice = sorted.slice(i, i + n);
    const key = slice
      .map(a => a.technique === 'custom' ? (a.custom_technique || 'custom') : a.technique)
      .join('→');
    if (!sequences[key]) sequences[key] = { count: 0, scored: 0, points: 0 };
    sequences[key].count++;
    const last = slice[slice.length - 1];
    if (last.outcome === 'punto') {
      sequences[key].scored++;
      sequences[key].points += last.points || 0;
    }
  }

  return Object.entries(sequences)
    .map(([seq, data]) => ({ seq, ...data }))
    .sort((a, b) => b.count - a.count || b.points - a.points);
}

/**
 * Analisi pattern cross-scambio.
 * Ogni "unità" è:
 *   - un'azione singola non taggata  → "Bandal chagi"
 *   - una sequenza taggata           → "[Push Kick → Bandal chagi]"
 * Poi cerca n-gram di unità, rilevando pattern del tipo:
 *   "Pk → Pk → [Pk → Bn]"
 *
 * @param {Array}    actions      - tutte le azioni della sessione
 * @param {string}   athlete      - 'blue' | 'red'
 * @param {number}   n            - lunghezza pattern (2 = coppie di unità, 3 = triple)
 * @param {Function} techResolver - (technique, custom_technique) => string label
 * @returns {Array} pattern ordinati per frequenza: { seq, count, scored, points }
 */
export function calculateCrossUnitPatterns(actions, athlete, n = 2, techResolver = (t, c) => c || t) {
  const myActions = actions
    .filter(a => a.athlete === athlete)
    .sort((a, b) => (a.timestamp_video || 0) - (b.timestamp_video || 0));

  if (!myActions.length) return [];

  // Costruisce la lista ordinata di unità
  const units = [];
  const processedSeqIds = new Set();

  myActions.forEach(a => {
    if (!a.sequence_id) {
      units.push({
        label:     techResolver(a.technique, a.custom_technique),
        timestamp: a.timestamp_video || 0,
        scored:    a.outcome === 'punto',
        points:    a.outcome === 'punto' ? (a.points || 0) : 0,
      });
    } else if (!processedSeqIds.has(a.sequence_id)) {
      processedSeqIds.add(a.sequence_id);
      const seqActions = myActions.filter(x => x.sequence_id === a.sequence_id);
      const inner      = seqActions.map(x => techResolver(x.technique, x.custom_technique)).join(' → ');
      const scored     = seqActions.some(x => x.outcome === 'punto');
      const points     = seqActions.reduce((s, x) => s + (x.outcome === 'punto' ? (x.points || 0) : 0), 0);
      units.push({
        label:     `[${inner}]`,
        timestamp: seqActions[0].timestamp_video || 0,
        scored,
        points,
      });
    }
  });

  units.sort((a, b) => a.timestamp - b.timestamp);
  if (units.length < n) return [];

  // N-gram sulle unità
  const patterns = {};
  for (let i = 0; i <= units.length - n; i++) {
    const slice = units.slice(i, i + n);
    const key   = slice.map(u => u.label).join(' → ');
    if (!patterns[key]) patterns[key] = { count: 0, scored: 0, points: 0 };
    patterns[key].count++;
    const last = slice[slice.length - 1];
    if (last.scored) {
      patterns[key].scored++;
      patterns[key].points += last.points;
    }
  }

  return Object.entries(patterns)
    .map(([seq, data]) => ({ seq, ...data }))
    .filter(p => p.count >= 2)                                    // pattern = almeno 2 occorrenze
    .sort((a, b) => b.count - a.count || b.points - a.points);
}

/**
 * Raggruppa le azioni esplicitamente taggate con sequence_id durante la registrazione.
 * Restituisce una lista di gruppi ordinati cronologicamente.
 * @param {Array}  actions  - tutte le azioni della sessione
 * @param {string} athlete  - 'blue' | 'red'
 * @returns {Array} Array di { seqId, acts, points, scored }
 */
export function calculateTaggedSequences(actions, athlete) {
  const myActions = actions
    .filter(a => a.athlete === athlete && a.sequence_id)
    .sort((a, b) => (a.timestamp_video || 0) - (b.timestamp_video || 0));

  if (!myActions.length) return [];

  const groups = {};
  myActions.forEach(a => {
    if (!groups[a.sequence_id]) groups[a.sequence_id] = [];
    groups[a.sequence_id].push(a);
  });

  return Object.entries(groups)
    .map(([seqId, acts]) => ({
      seqId,
      acts,
      points: acts.reduce((s, a) => s + (a.outcome === 'punto' ? (a.points || 0) : 0), 0),
      scored: acts.filter(a => a.outcome === 'punto').length,
    }))
    .sort((a, b) => (a.acts[0].timestamp_video || 0) - (b.acts[0].timestamp_video || 0));
}

/**
 * Calcola statistiche per round
 */
export function calculateRoundStats(actions, penalties, numRounds) {
  const rounds = [];
  for (let r = 1; r <= numRounds; r++) {
    const rActions   = actions.filter(a => a.round_number === r);
    const rPenalties = penalties.filter(p => p.round_number === r);
    const blueScore  = rActions.filter(a => a.athlete === 'blue' && a.outcome === 'punto').reduce((s,a) => s+(a.points||0),0)
                     + rPenalties.filter(p => p.athlete === 'red').length;
    const redScore   = rActions.filter(a => a.athlete === 'red'  && a.outcome === 'punto').reduce((s,a) => s+(a.points||0),0)
                     + rPenalties.filter(p => p.athlete === 'blue').length;
    rounds.push({ round: r, blueScore, redScore });
  }
  return rounds;
}
